import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, GraduationCap } from 'lucide-react';
import { createTeacher, updateTeacher, TeacherDetail } from '../api';
import { useToast } from '../../../context/ToastContext';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

// Mismo criterio que el backend (slugUser): minúsculas, sin acentos ni símbolos.
function slugUser(s: string) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Modal de alta/edición de profesor. `teacher` presente = modo edición. onSaved(id) tras guardar. */
export default function TeacherForm({ teacher, onClose, onSaved }: {
  teacher?: TeacherDetail | null;
  onClose: () => void;
  onSaved: (id: number) => void;
}) {
  const toast = useToast();
  const editing = !!teacher;
  const [firstname, setFirstname] = useState(teacher?.firstname || '');
  const [lastname, setLastname] = useState(teacher?.lastname || '');
  const [username, setUsername] = useState(teacher?.username || '');
  const [email, setEmail] = useState(teacher?.email || '');
  const [prefix, setPrefix] = useState(teacher?.phone_prefix || '52');
  const [phone, setPhone] = useState(teacher?.phone_number || '');
  const [dob, setDob] = useState(teacher?.dob ? teacher.dob.slice(0, 10) : '');
  const [notes, setNotes] = useState(teacher?.notes || '');
  const [saving, setSaving] = useState(false);
  const userTouched = useRef(editing); // en alta, autosugerir hasta que el admin lo edite

  // Autosugerir usuario desde nombre+apellido mientras no lo toquen (solo en alta).
  useEffect(() => {
    if (!editing && !userTouched.current) {
      setUsername(slugUser(firstname.slice(0, 1) + lastname));
    }
  }, [firstname, lastname, editing]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  async function submit() {
    if (!firstname.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        username: slugUser(username) || undefined,
        email: email.trim() || null,
        phone_prefix: phone.trim() ? prefix.trim() : null,
        phone_number: phone.trim() || null,
        dob: dob || null,
        notes: notes.trim() || null,
      };
      if (editing) {
        await updateTeacher(teacher!.account_id, payload);
        toast.success('Profesor actualizado');
        onSaved(teacher!.account_id);
      } else {
        const r = await createTeacher(payload);
        toast.success(`Profesor dado de alta (usuario: ${r.data.username})`);
        onSaved(r.data.account_id);
      }
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo guardar');
    } finally { setSaving(false); }
  }

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="modal-panel max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between border-b border-line bg-surface px-5 py-3.5">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-content">
            <GraduationCap size={18} className="text-brand-blue" /> {editing ? 'Editar profesor' : 'Nuevo profesor'}
          </h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Nombre *</label>
              <input className={inputCls} value={firstname} onChange={(e) => setFirstname(e.target.value)} autoFocus placeholder="Nombre" />
            </div>
            <div>
              <label className={labelCls}>Apellidos</label>
              <input className={inputCls} value={lastname} onChange={(e) => setLastname(e.target.value)} placeholder="Apellidos" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Usuario para entrar</label>
            <input className={inputCls} value={username}
              onChange={(e) => { userTouched.current = true; setUsername(slugUser(e.target.value)); }}
              placeholder="se autogenera del nombre" />
            <p className="mt-1 text-xs text-content-3">Con esto inicia sesión el profesor. Solo letras y números.</p>
          </div>

          <div>
            <label className={labelCls}>Correo</label>
            <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@ejemplo.com (opcional)" />
          </div>

          <div>
            <label className={labelCls}>Teléfono</label>
            <div className="flex gap-2">
              <input className={`${inputCls} w-16 shrink-0`} value={prefix} onChange={(e) => setPrefix(e.target.value.replace(/\D/g, ''))} aria-label="Prefijo" />
              <input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="10 dígitos (opcional)" inputMode="numeric" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Fecha de nacimiento</label>
              <input className={inputCls} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notas</label>
            <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas internas (opcional)" />
          </div>

          {!editing && (
            <p className="text-xs text-content-3">El acceso del profesor (contraseña) se genera aparte con el botón “Generar acceso” cuando lo decidas.</p>
          )}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-line bg-surface px-5 py-3.5">
          <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">Cancelar</button>
          <button onClick={submit} disabled={saving || !firstname.trim()}
            className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:opacity-40">
            {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear profesor'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
